import { Box, Heading, Stack, Text } from "@chakra-ui/react";
import React, { useContext } from "react";
import { ParticipantFeeSummary } from "../../types/fee.types";
import CurrencyContext from "../../context/CurrencyContext";
import { Trans } from "@lingui/macro";
import { CongressButtonScheme } from "../common/CongressButton";
import { formatPrice } from "../../utils/price-formatter";

interface FeeSummaryProps {
  feeSummary: ParticipantFeeSummary;
  originalFeeSummary?: ParticipantFeeSummary;
}

export const FeeSummary: React.FC<FeeSummaryProps> = ({
  feeSummary,
  originalFeeSummary,
}) => {
  const currency = useContext(CurrencyContext);
  const hasChanges = originalFeeSummary !== undefined;
  const nameChanged =
    hasChanges &&
    originalFeeSummary &&
    originalFeeSummary.name !== feeSummary?.name;
  const priceChanged =
    hasChanges &&
    originalFeeSummary &&
    originalFeeSummary.totalPrice !== feeSummary?.totalPrice;

  return (
    <Box
      borderRadius={hasChanges ? "lg" : "none"}
      _before={
        hasChanges
          ? {
              content: '""',
              position: "absolute",
              inset: "0px",
              zIndex: -1,
              borderRadius: "lg",
              filter: "blur(4px)",
              pointerEvents: "none",
              boxShadow: `
                -3px -3px 8px rgba(245, 101, 101, 0.15),
                1.5px -2px 9px rgba(237, 100, 166, 0.15),
                -4.5px  1.5px 10px rgba(237, 137,  54, 0.15),
                1.5px  3px 8px rgba(237, 100, 166, 0.125),
                -3px 5.5px 8px rgba(245, 101, 101, 0.125),
                3px -3px 9px rgba(237, 137,  54, 0.125)
              `,
            }
          : undefined
      }
      zIndex={0}
      position="relative"
    >
      <Stack
        px={0}
        py={3}
        justifyContent={{
          base: "flex-start",
          md: "space-between",
        }}
        direction={{
          base: "column",
          md: "row",
        }}
        alignItems={{ md: "center" }}
      >
        <Heading size={"md"} w={{ md: "30%" }}>
          <Trans>Attendance Fee</Trans>
        </Heading>
        <Heading
          size={"md"}
          bgGradient={CongressButtonScheme.GREEN_SCHEME}
          bgClip="text"
          textAlign="start"
          w={{ md: "37%" }}
        >
          {nameChanged ? (
            <>
              <Text as="span" color="green.500" fontWeight={600}>
                {feeSummary.name}
              </Text>
              {originalFeeSummary && (
                <Text
                  as="span"
                  color="red.500"
                  ml={2}
                  textDecoration="line-through"
                >
                  ({originalFeeSummary.name})
                </Text>
              )}
            </>
          ) : (
            feeSummary?.name
          )}
        </Heading>
        <Heading
          textAlign={{ base: "left", md: "right" }}
          size={"xl"}
          w={{ md: "40%" }}
        >
          {priceChanged ? (
            <>
              <Text as="span" color="green.500" fontWeight={600}>
                {formatPrice(feeSummary.totalPrice)} {currency}
              </Text>
              {originalFeeSummary && (
                <Text
                  as="span"
                  color="red.500"
                  ml={2}
                  textDecoration="line-through"
                  fontSize="lg"
                >
                  ({formatPrice(originalFeeSummary.totalPrice)} {currency})
                </Text>
              )}
            </>
          ) : (
            <>
              {formatPrice(feeSummary?.totalPrice)} {currency}
            </>
          )}
        </Heading>
      </Stack>
    </Box>
  );
};
